import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Divider,
  Link,
  Tooltip,
  IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { supabase } from '../lib/supabase';

const Settings = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    industry: '',
    reviewLink: ''
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      console.log("====== FETCHING SETTINGS - START ======");
      console.log("Fetching current settings from server...");
      // Add cache-busting parameter to ensure we get fresh data
      const timestamp = new Date().getTime();
      const response = await api.get(`/api/settings?_=${timestamp}`);
      console.log("Received settings response:", response.data);
      
      // Log the company data specifically
      if (response.data.company) {
        console.log("Company data received:", {
          name: response.data.company.name,
          industry: response.data.company.industry,
          reviewLink: response.data.company.reviewLink
        });
        
        // Specifically log the review link
        const reviewLink = response.data.company.reviewLink;
        console.log(`Review link in response: type=${typeof reviewLink}, value="${reviewLink}", length=${reviewLink ? reviewLink.length : 0}`);
        
        // Check for potential issues with the review link
        if (reviewLink) {
          if (!reviewLink.includes('.')) {
            console.warn("Review link may be invalid - no domain: ", reviewLink);
          }
          if (!reviewLink.startsWith('http')) {
            console.warn("Review link missing http/https prefix: ", reviewLink);
          }
        }
        
        // Normalize the review link to always ensure it has https:// prefix
        let normalizedReviewLink = reviewLink || '';
        if (normalizedReviewLink && !normalizedReviewLink.startsWith('http')) {
          console.log(`Adding https:// prefix to review link in frontend: '${normalizedReviewLink}'`);
          normalizedReviewLink = `https://${normalizedReviewLink}`;
        }
        
        // Update the formData
        setFormData({
          name: response.data.name || '',
          companyName: response.data.company?.name || '',
          industry: response.data.company?.industry || '',
          reviewLink: normalizedReviewLink
        });
        
        console.log("Form data updated with normalized settings:", {
          name: response.data.name || '',
          companyName: response.data.company?.name || '',
          industry: response.data.company?.industry || '',
          reviewLink: normalizedReviewLink
        });
      } else {
        console.log("No company data in response");
        setFormData({
          name: response.data.name || '',
          companyName: '',
          industry: '',
          reviewLink: ''
        });
        
        console.log("Form data updated without company data:", {
          name: response.data.name || '',
          companyName: '',
          industry: '',
          reviewLink: ''
        });
      }
      
      console.log("====== FETCHING SETTINGS - END ======");
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Det gick inte att ladda inställningar. Försök igen senare.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Log the unformatted data for debugging
      console.log("Form data before formatting:", {
        name: formData.name,
        companyName: formData.companyName,
        industry: formData.industry,
        reviewLink: formData.reviewLink
      });
      
      // Format the reviewLink if needed
      const formattedData = {
        ...formData,
        reviewLink: formData.reviewLink ? 
          (formData.reviewLink.startsWith('http') ? formData.reviewLink : `https://${formData.reviewLink}`) 
          : ''
      };
      
      console.log("Form data after formatting:", {
        name: formattedData.name,
        companyName: formattedData.companyName,
        industry: formattedData.industry,
        reviewLink: formattedData.reviewLink,
        reviewLinkType: typeof formattedData.reviewLink
      });

      // Save settings to the backend
      console.log("Saving settings:", formattedData);
      const settingsResponse = await api.post('/api/settings', formattedData);
      console.log("Settings saved response:", settingsResponse.data);
      
      // After successfully saving settings, trigger template personalization
      try {
        console.log("Settings saved, personalizing templates...");
        console.log("Auth state before personalization:", await supabase.auth.getSession());
        
        const templateResponse = await api.post('/api/personalize', formattedData);
        console.log("Templates personalized:", templateResponse.data);
        
        // If templates were returned, update the UI
        if (templateResponse.data && templateResponse.data.templates) {
          console.log("Received personalized templates:", templateResponse.data.templates);
        } else {
          console.warn("No templates returned from personalization endpoint");
        }
      } catch (templateErr) {
        console.error("Error personalizing templates:", templateErr);
        console.error("Response status:", templateErr.response?.status);
        console.error("Response data:", templateErr.response?.data);
        // Don't show error to user - we'll still mark settings as saved
      }
      
      setSuccess(true);
      
      // Add a delay before refreshing to ensure Supabase has processed the changes
      console.log("Waiting for Supabase to process changes before refreshing...");
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Refresh settings to confirm changes were saved
      console.log("Refreshing settings data...");
      await fetchSettings();
      console.log("Settings refreshed successfully");
      
      // Compare the refreshed data with what we submitted
      console.log("Comparing submitted vs. refreshed data:");
      console.log("Submitted:", {
        name: formattedData.name,
        companyName: formattedData.companyName,
        industry: formattedData.industry,
        reviewLink: formattedData.reviewLink
      });
      console.log("Refreshed:", {
        name: formData.name,
        companyName: formData.companyName,
        industry: formData.industry,
        reviewLink: formData.reviewLink
      });
      
      // Check if the reviewLink was properly saved
      if (formattedData.reviewLink !== formData.reviewLink) {
        console.warn("Review link mismatch after refresh!");
        console.warn(`Expected: "${formattedData.reviewLink}", Got: "${formData.reviewLink}"`);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Det gick inte att spara inställningar. Försök igen.');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = () => {
    if (formData.reviewLink) {
      navigator.clipboard.writeText(formData.reviewLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Inställningar
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Inställningar sparade!
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Personlig Information
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Ditt namn kommer att visas i kommunikationen med kunder.
            </Typography>
            <TextField
              fullWidth
              label="Namn"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              placeholder="Ditt namn (valfritt)"
            />
            
            <Divider sx={{ my: 3 }} />
            
            <Typography variant="h6" gutterBottom>
              Företagsinformation
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Dessa uppgifter kan användas i meddelandemallar som skickas till dina kunder.
            </Typography>
            <TextField
              fullWidth
              label="Företagsnamn"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              margin="normal"
              placeholder="Ditt företags namn (valfritt)"
            />
            <TextField
              fullWidth
              label="Bransch"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              margin="normal"
              placeholder="Din bransch (valfritt)"
            />
            <TextField
              fullWidth
              label="Recensionslänk"
              name="reviewLink"
              value={formData.reviewLink}
              onChange={handleChange}
              margin="normal"
              placeholder="https://g.page/example eller https://maps.app.goo.gl/example"
              helperText="Denna länk skickas till kunder när de ger positiv feedback. Använd en komplett URL som börjar med https://"
              InputProps={{
                endAdornment: formData.reviewLink ? (
                  <Tooltip title={copied ? "Kopierad!" : "Kopiera länk"}>
                    <IconButton onClick={handleCopyLink} edge="end">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ) : null
              }}
              error={formData.reviewLink && !formData.reviewLink.includes('.')}
            />
            {formData.reviewLink && (
              <Box mt={1}>
                <Link 
                  href={formData.reviewLink.startsWith('http') ? formData.reviewLink : `https://${formData.reviewLink}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    // Prevent default if URL is clearly invalid
                    if (!formData.reviewLink.includes('.')) {
                      e.preventDefault();
                      setError('Ogiltig URL. Kontrollera att recensionslänken är korrekt.');
                    }
                  }}
                >
                  Testa länken
                </Link>
              </Box>
            )}
          </CardContent>
        </Card>

        <Box sx={{ mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={saving}
            sx={{ minWidth: 200 }}
          >
            {saving ? 'Sparar...' : 'Spara inställningar'}
          </Button>
        </Box>
      </form>

      <Divider sx={{ my: 4 }} />
      
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault()
              navigate('/terms')
            }}
            sx={{ 
              textDecoration: 'underline',
              color: 'primary.main',
              cursor: 'pointer'
            }}
          >
            Läs våra användarvillkor
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default Settings; 